import React, { useState, useEffect } from "react";
import { View, Pressable } from "react-native-web";
import { useLocation, useNavigate } from "react-router-dom";
import CustomText from "../../components/atoms/CustomText";
import { colors, spacing, } from "../../utils/styling";
import { useResponsive } from "../../hooks/useResponsive";
import { useTransactionId } from "../../hooks/useTransactionId";
import {
} from "../../utils/category_mappings";
import {
  resolveCoreSection,
  resolveWorkbookExercises,
} from "../../utils/quizinfo/personalityContent";
import { createCheckoutSession, getSubscriptionStatus, cancelSubscription, createCustomerPortal } from "../../lib/stripe";
import { usePaymentStatus } from "../../hooks/usePaymentStatus";
import { PDFDownloadLink } from "@react-pdf/renderer";
import PersonalityTestPDF from "../../components/common/PersonalityTestPDF";
import * as Toast from "@radix-ui/react-toast";

import ConnectProfessionalModal from "../../components/popups/ConnectProfessionalModal";
import ResultsGraph from "../../components/common/ResultsGraph";
import CatContributionBar from "../../components/common/CatContributionBar";
import PaymentOverlay from "./PaymentOverlay";
import PaywallOverlay from "../../components/common/PaywallOverlay";
import GuidedActionPlan from "../../components/common/GuidedActionPlan";
import { loadDataWithVersionCheck } from "../../utils/versionManager";

interface ResultsData {
  // The three sub-personality fields below are OPTIONAL as of 2026-08-20
  // (ClickUp 86bb0f9vz). New results never carry them — the sub-quiz is gone —
  // but results stored in localStorage before that date still do, and
  // `specificDominantCategory` is the key that resolves a pre-existing user's
  // workbook exercises. Do not delete them: that would strip the field on the
  // way through and silently empty an existing user's saved workbook.
  answers: number[];
  specificAnswers?: number[];
  categoryScores: Record<string, number>;
  specificCategoryScores?: Record<string, number>;
  dominantCategory: string;
  specificDominantCategory?: string;
  sfAccount?: string | null;
  quizId?: number;
  leadId?: number;
  email?: string;
}

interface SubscriptionError {
  type: 'network' | 'not_found' | 'invalid_status' | 'unknown';
  message: string;
}

const CATEGORY_COLORS: Record<string, string> = {
  "Dream Chaser": "#00A4EF",
  "Wealth Architect": "#00B3C2",
  "Safety Seeker": "#076652",
  "Generous Altruist": "#9933FF",
  "Freedom Finder": "#FF00FF",
};


export default function MainResults() {
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [showPaymentPending, setShowPaymentPending] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [showSubscriptionManagement, setShowSubscriptionManagement] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState<SubscriptionError | null>(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);
  const { isMobile } = useResponsive();
  const location = useLocation();
  const navigate = useNavigate();

  // Get the results data from location state
  let resultsData =
    location.state?.from !== "/quiz"
      ? (location.state as ResultsData)
      : (location.state.quizResults as ResultsData);

  if (!resultsData) {
    // resultsData not in state, check local storage
    const stored = localStorage.getItem("mpay_resultsData");
    if (stored) {
      try {
        let parsedData = JSON.parse(stored);
        
        // Apply migration if needed
        parsedData = loadDataWithVersionCheck(parsedData, 'localStorage');
        
        resultsData = parsedData;
      } catch (e) {
        console.error("Error parsing/migrating localStorage data:", e);
      }
    } else {
      console.log('No localStorage data found');
    }
  }

  const defaultResultsData: ResultsData = {
    answers: [],
    specificAnswers: [],
    categoryScores: {},
    specificCategoryScores: {},
    dominantCategory: "",
    specificDominantCategory: "",
    sfAccount: null,
    quizId: undefined,
    leadId: undefined,
    email: undefined,
  };

  // Use resultsData if available, otherwise use defaults
  const safeResultsData = resultsData || defaultResultsData;

  const {
    email,
    dominantCategory,
    answers,
    categoryScores,
    specificDominantCategory,
    sfAccount,
    quizId,
  } = safeResultsData;
  
  const { leadId } = safeResultsData;
  const {
    status: paymentStatus,
    loading: paymentLoading,
    error: paymentError,
    stopPolling,
  } = usePaymentStatus(paymentId, showPaymentPending);

  // Use the custom hook to get transaction data
  const {
    transactionData,
    loading: transactionLoading,
    error: transactionError,
  } = useTransactionId(quizId);
  // Check if this user has done a payment or has active subscription
  const [hasPaid, setHasPaid] = useState<boolean>(() => {
    // Check localStorage first for payment success
    const paymentSuccess = localStorage.getItem("payment_success") === "true";
    const couponRedeemed =
      localStorage.getItem("mpay_coupon_redeemed") !== null;
    const subscriptionActive = localStorage.getItem("subscription_active") === "true";
    return !!sfAccount || paymentSuccess || couponRedeemed || subscriptionActive;
  });
  
  // Paywall state.
  // Individual results paywall REMOVED 2026-06-11 (ClickUp 86badfk4g — advisor-portal pivot P2):
  // the full personality content + exercises are free for individuals. Revenue comes from the
  // future FIRM subscription, not the individual unlock — so the gate is forced off here while the
  // Stripe/subscription code below is kept dormant (not deleted) for that future firm flow.
  const [showPaywall, setShowPaywall] = useState(false);

  // Check for payment_id in URL params (for payment recovery)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlPaymentId = urlParams.get("payment_id");
    if (urlPaymentId) {
      setPaymentId(urlPaymentId);
      setShowPaymentPending(true);
    }
  }, []);

  // Save resultsData to localStorage on mount and whenever it changes
  useEffect(() => {
    if (resultsData) {
      localStorage.setItem("mpay_resultsData", JSON.stringify(resultsData));
    }
  }, [resultsData]);

  // Pass transaction data to QuizHeader via global object
  useEffect(() => {
    if (transactionData) {
      // @ts-ignore
      window.transactionData = transactionData;
    }
    return () => {
      // @ts-ignore
      delete window.transactionData;
    };
  }, [transactionData]);

  // Enhanced function to check subscription status with better error handling
  const checkSubscriptionStatus = async (customerId: string): Promise<void> => {
    setIsLoadingSubscription(true);
    setSubscriptionError(null);
    
    try {
      console.log(`🔄 Checking subscription status for customer: ${customerId}`);
      const subStatus = await getSubscriptionStatus(customerId);
      
      console.log(`📊 Subscription status received:`, subStatus);
      
      setSubscriptionStatus(subStatus.status);
      setSubscriptionId(subStatus.subscription_id || null);
      
      if (subStatus.status === 'active') {
        localStorage.setItem("subscription_active", "true");
        setHasPaid(true);
        console.log('✅ Subscription is active, user has paid access');
      } else if (subStatus.status === 'cancelled' || subStatus.status === 'past_due') {
        localStorage.removeItem("subscription_active");
        if (subStatus.status === 'past_due') {
          console.log('⚠️ Subscription is past due');
        } else {
          console.log('❌ Subscription is cancelled');
        }
      } else if (subStatus.status === 'incomplete' || (subStatus.status as string) === 'incomplete_expired') {
        localStorage.removeItem("subscription_active");
        console.log('⚠️ Subscription is incomplete');
      }
      
    } catch (error: any) {
      console.error('❌ Error checking subscription status:', error);
      
      // Categorize different types of errors
      if (error.message?.includes('Network Error') || error.message?.includes('fetch')) {
        setSubscriptionError({
          type: 'network',
          message: 'Unable to check subscription status. Please check your internet connection.'
        });
      } else if (error.message?.includes('404') || error.message?.includes('not found')) {
        setSubscriptionError({
          type: 'not_found',
          message: 'Subscription not found. If you just purchased, please wait a moment and try again.'
        });
      } else if (error.message?.includes('Invalid subscription status')) {
        setSubscriptionError({
          type: 'invalid_status',
          message: 'There\'s an issue with your subscription. Please contact support.'
        });
      } else {
        setSubscriptionError({
          type: 'unknown',
          message: 'Unable to check subscription status. Please try again later.'
        });
      }
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  // Polling function for subscription status
  const startSubscriptionPolling = (customerId: string) => {
    console.log('🔄 Starting subscription status polling');
    
    // Initial check
    checkSubscriptionStatus(customerId);
    
    // Set up polling interval (every 30 seconds)
    const pollInterval = setInterval(() => {
      checkSubscriptionStatus(customerId);
    }, 30000); // 30 seconds
    
    // Store interval ID to clean up later
    return pollInterval;
  };

  // Enhanced subscription status checking with polling
  useEffect(() => {
    const storedCustomerId = localStorage.getItem("stripe_customer_id");
    if (storedCustomerId) {
      setCustomerId(storedCustomerId);
      
      // Start polling for subscription status
      const pollInterval = startSubscriptionPolling(storedCustomerId);
      
      // Cleanup interval on component unmount
      return () => {
        console.log('🛑 Stopping subscription status polling');
        clearInterval(pollInterval);
      };
    }
  }, []);

  // Update hasPaid when transactionData or subscription status changes
  useEffect(() => {
    const paymentSuccess = localStorage.getItem("payment_success") === "true";
    const couponRedeemed =
      localStorage.getItem("mpay_coupon_redeemed") !== null;
    const subscriptionActive = localStorage.getItem("subscription_active") === "true";
    const newHasPaid =
      !!sfAccount ||
      paymentSuccess ||
      couponRedeemed ||
      subscriptionActive ||
      (transactionData?.status === "found" && !!transactionData.transaction_id);
    
    // Only update hasPaid if it's actually changing to avoid unnecessary re-renders
    if (newHasPaid !== hasPaid) {
      setHasPaid(newHasPaid);
    }

    // If we found a transaction but no localStorage flag, set it
    if (transactionData?.status === "found" && !paymentSuccess) {
      localStorage.setItem("payment_success", "true");
    }
  }, [transactionData, sfAccount, hasPaid, email, quizId, subscriptionStatus]);

  // Individual paywall removed (86badfk4g): never re-gate content based on payment status.
  // (hasPaid is still computed above for the dormant subscription-management UI.)
  useEffect(() => {
    setShowPaywall(false);
  }, [hasPaid]);

  // Handle payment completion (both one-time and subscription)
  useEffect(() => {
    if (paymentStatus?.status === "completed") {
      setShowPaymentPending(false);
      setShowPaymentSuccess(true);
      setHasPaid(true);
      stopPolling();

      // For subscriptions, we need to get the customer ID from the session
      // The backend should provide this information, but for now we'll handle both flows
      if (paymentStatus.transaction_id) {
        // Check if this is a subscription by trying to get customer ID
        // This is a simplified approach - in practice, you might want to modify the backend 
        // to return more session details including customer_id
        localStorage.setItem("payment_success", "true");
      }

      // Show success toast
      setTimeout(() => {
        setShowPaymentSuccess(false);
      }, 3000);
    }
  }, [paymentStatus, stopPolling]);

  // Periodically check localStorage for updated results (in case user navigates back from Stripe)
  useEffect(() => {
    const checkForUpdatedResults = () => {
      const stored = localStorage.getItem("mpay_resultsData");
      if (stored && !resultsData) {
        try {
          const parsedData = JSON.parse(stored);
          if (parsedData && parsedData.dominantCategory) {
            console.log('🔄 Found updated results in localStorage, refreshing page');
            window.location.reload();
          }
        } catch (e) {
          console.error("Error parsing stored results:", e);
        }
      }
    };

    // Check immediately and then every 2 seconds for the first 10 seconds
    // This handles the case where user comes back from Stripe and we need to pick up the localStorage data
    const intervals = [0, 2000, 4000, 6000, 8000, 10000];
    const timers = intervals.map(delay => 
      setTimeout(checkForUpdatedResults, delay)
    );

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [resultsData]);

  // Handle unlock action
  const handleUnlock = async (redeemed: boolean) => {
    if (redeemed) {
      // user used coupon code, show results instantly
      setHasPaid(true);
      localStorage.setItem("mpay_coupon_redeemed", "true");
    } else {
      // user clicked on "Unlock results", navigate them to Stripe so that they can pay
      try {
        if (!quizId || !email) {
          console.error("Missing quizId or email");
          return;
        }

        console.log("Creating checkout session");
        const paymentResponse = await createCheckoutSession({
          quiz_id: quizId,
          email: email,
        });
        console.log("Payment link object is", paymentResponse);
        setPaymentId(paymentResponse.session_id);
        setPaymentUrl(paymentResponse.checkout_url);
        setShowPaymentPending(true);

        // Open payment link in new tab
        window.open(paymentResponse.checkout_url, "_blank");

        // Update URL with payment_id for recovery
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set("payment_id", paymentResponse.session_id);
        window.history.replaceState({}, "", newUrl.toString());
      } catch (error: any) {
        console.error("Failed to create payment link:", error);
      }
    }
  };

  const handleOpenPayment = () => {
    if (paymentUrl) {
      window.open(paymentUrl, "_blank");
    }
  };

  const handleCancelPayment = () => {
    stopPolling();
    setPaymentId(null);
    setPaymentUrl(null);
    setShowPaymentPending(false);

    // Remove payment_id from URL
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete("payment_id");
    window.history.replaceState({}, "", newUrl.toString());

    // Don't remove payment_success from localStorage here since user might have actually paid
    // The payment status polling will handle setting it correctly
  };

  // Enhanced error handling for subscription cancellation
  const handleCancelSubscription = async () => {
    if (!subscriptionId) {
      alert('Unable to cancel subscription - subscription ID not found.');
      return;
    }
    
    setIsLoadingSubscription(true);
    setSubscriptionError(null);
    
    try {
      console.log(`🚫 Cancelling subscription: ${subscriptionId}`);
      const result = await cancelSubscription(subscriptionId);
      
      if (result.status === 'success') {
        // Update subscription status immediately
        setSubscriptionStatus('cancelled');
        localStorage.removeItem("subscription_active");
        setHasPaid(false);
        setShowSubscriptionManagement(false);
        
        // Show success message
        alert('Subscription cancelled successfully. You can continue to access your results until the end of your current billing period.');
        
        // Trigger immediate status check to get updated info
        if (customerId) {
          checkSubscriptionStatus(customerId);
        }
      } else {
        throw new Error(result.message || 'Failed to cancel subscription');
      }
    } catch (error: any) {
      console.error('❌ Error cancelling subscription:', error);
      
      let errorMessage = 'Failed to cancel subscription. ';
      if (error.message?.includes('Network Error')) {
        errorMessage += 'Please check your internet connection and try again.';
      } else if (error.message?.includes('already cancelled')) {
        errorMessage += 'This subscription has already been cancelled.';
      } else {
        errorMessage += 'Please try again or contact support if the problem persists.';
      }
      
      alert(errorMessage);
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  // Enhanced error handling for customer portal
  const handleOpenCustomerPortal = async () => {
    if (!customerId) {
      alert('Unable to open customer portal - customer ID not found.');
      return;
    }
    
    setIsLoadingSubscription(true);
    setSubscriptionError(null);
    
    try {
      console.log(`🏢 Opening customer portal for customer: ${customerId}`);
      const result = await createCustomerPortal(customerId, window.location.href);
      
      if (result.status === 'success') {
        // Open customer portal in new tab
        window.open(result.portal_url, '_blank');
      } else {
        throw new Error('Failed to open customer portal');
      }
    } catch (error: any) {
      console.error('❌ Error opening customer portal:', error);
      
      let errorMessage = 'Failed to open customer portal. ';
      if (error.message?.includes('Network Error')) {
        errorMessage += 'Please check your internet connection and try again.';
      } else {
        errorMessage += 'Please try again or contact support if the problem persists.';
      }
      
      alert(errorMessage);
    } finally {
      setIsLoadingSubscription(false);
    }
  };
  
  // Content resolution — see utils/quizinfo/personalityContent.ts.
  //
  // Until 2026-08-20 this page rendered SUB-personality content with the core
  // personality as a fallback. The sub-personality quiz is gone (ClickUp
  // 86bb0f9vz) and every content block below now reads the CORE section.
  //
  // ⛔ Still never index CATEGORY_SECTIONS[...] directly (ADR 2026-07-20).
  // Stored results outlive the content maps, and a bare index on a retired
  // personality name threw here and white-screened the whole app.
  const mainSection = resolveCoreSection(dominantCategory, "MainResults");

  // Workbook exercises are the ONE thing still keyed to the old sub-types —
  // they exist only on SPECIFIC_CATEGORY_SECTIONS. A user who took the quiz
  // before 2026-08-20 has a stored sub-personality AND saved answers on the
  // server, so their workbook keeps working. A new result has none, and the
  // empty case is handled explicitly below rather than rendered as a blank.
  const specificExercises = resolveWorkbookExercises(specificDominantCategory, dominantCategory);

  // All possible main categories - ensure all are included even if 0%
  const ALL_MAIN_CATEGORIES = Object.keys(CATEGORY_COLORS);
  const completeMainCategoryScores = ALL_MAIN_CATEGORIES.reduce((acc, category) => {
    acc[category] = categoryScores[category] ?? 0;
    return acc;
  }, {} as Record<string, number>);

  // Sort categories by percentage (descending)
  const sortedCategories = ALL_MAIN_CATEGORIES.sort(
    (a, b) => (completeMainCategoryScores[b] ?? 0) - (completeMainCategoryScores[a] ?? 0)
  );

  // Enhanced subscription management UI with error handling
  const renderSubscriptionManagement = () => {
    if (!customerId) return null;
    
    const getStatusColor = (status: string) => {
      switch (status) {
        case 'active': return colors.success;
        case 'cancelled': return colors.warning;
        case 'past_due': return colors.danger;
        case 'incomplete': return colors.warning;
        default: return colors.secondary;
      }
    };
    
    const getStatusText = (status: string) => {
      switch (status) {
        case 'active': return 'Active';
        case 'cancelled': return 'Cancelled';
        case 'past_due': return 'Payment Past Due';
        case 'incomplete': return 'Incomplete';
        case 'incomplete_expired': return 'Expired';
        default: return status;
      }
    };
    
    return (
      <View style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}>
        <View
          style={{
            backgroundColor: colors.white,
            padding: spacing.xl,
            borderRadius: 16,
            maxWidth: 500,
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
          }}
        >
          <CustomText
            style={{
              fontSize: 24,
              fontWeight: '700',
              color: colors.dark,
              fontFamily: 'Nunito',
              textAlign: 'center',
              marginBottom: spacing.md,
            }}
          >
            Subscription Management
          </CustomText>
          
          {/* Loading State */}
          {isLoadingSubscription && (
            <View style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: spacing.lg,
            }}>
              <CustomText style={{
                fontSize: 16,
                color: colors.gray[600],
                fontFamily: 'Nunito',
              }}>
                Checking subscription status...
              </CustomText>
            </View>
          )}
          
          {/* Error State */}
          {subscriptionError && (
            <View style={{
              backgroundColor: colors.gray[100],
              padding: spacing.md,
              borderRadius: 8,
              marginBottom: spacing.md,
            }}>
              <CustomText style={{
                fontSize: 16,
                fontWeight: '600',
                color: colors.danger,
                fontFamily: 'Nunito',
                marginBottom: spacing.xs,
              }}>
                ⚠️ Error
              </CustomText>
              <CustomText style={{
                fontSize: 14,
                color: colors.gray[700],
                fontFamily: 'Nunito',
                marginBottom: spacing.sm,
              }}>
                {subscriptionError.message}
              </CustomText>
              <Pressable 
                style={({ hovered }: { hovered: boolean }) => ({
                  backgroundColor: hovered ? colors.primary : colors.secondary,
                  paddingVertical: spacing.xs,
                  paddingHorizontal: spacing.sm,
                  borderRadius: 6,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                })}
                onPress={() => customerId && checkSubscriptionStatus(customerId)}
              >
                <CustomText style={{
                  color: colors.white,
                  fontSize: 14,
                  fontWeight: '600',
                  fontFamily: 'Nunito',
                }}>
                  Retry
                </CustomText>
              </Pressable>
            </View>
          )}
          
          {/* Subscription Status */}
          {subscriptionStatus && !isLoadingSubscription && (
            <View style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: spacing.lg,
            }}>
              <CustomText style={{
                fontSize: 16,
                color: colors.gray[700],
                fontFamily: 'Nunito',
                marginRight: spacing.xs,
              }}>
                Status:
              </CustomText>
              <CustomText style={{
                fontSize: 16,
                fontWeight: '600',
                color: getStatusColor(subscriptionStatus),
                fontFamily: 'Nunito',
              }}>
                {getStatusText(subscriptionStatus)}
              </CustomText>
            </View>
          )}
          
          {/* Action Buttons */}
          <View style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing.sm,
            marginBottom: spacing.lg,
          }}>
            <Pressable 
              style={({ hovered }: { hovered: boolean }) => ({
                backgroundColor: hovered ? colors.primary : colors.secondary,
                paddingVertical: spacing.md,
                paddingHorizontal: spacing.lg,
                borderRadius: 8,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                opacity: isLoadingSubscription ? 0.6 : 1,
              })}
              onPress={handleOpenCustomerPortal}
              disabled={isLoadingSubscription}
            >
              <CustomText style={{
                color: colors.white,
                fontSize: 16,
                fontWeight: '600',
                fontFamily: 'Nunito',
                textAlign: 'center',
              }}>
                {isLoadingSubscription ? 'Loading...' : 'Manage Subscription'}
              </CustomText>
            </Pressable>
            
            {subscriptionStatus === 'active' && (
              <Pressable 
                style={({ hovered }: { hovered: boolean }) => ({
                  backgroundColor: hovered ? '#c53030' : colors.danger,
                  paddingVertical: spacing.md,
                  paddingHorizontal: spacing.lg,
                  borderRadius: 8,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: isLoadingSubscription ? 0.6 : 1,
                })}
                onPress={handleCancelSubscription}
                disabled={isLoadingSubscription}
              >
                <CustomText style={{
                  color: colors.white,
                  fontSize: 16,
                  fontWeight: '600',
                  fontFamily: 'Nunito',
                  textAlign: 'center',
                }}>
                  {isLoadingSubscription ? 'Loading...' : 'Cancel Subscription'}
                </CustomText>
              </Pressable>
            )}
          </View>
          
          <Pressable 
            style={({ hovered }: { hovered: boolean }) => ({
              backgroundColor: hovered ? colors.gray[300] : colors.gray[200],
              paddingVertical: spacing.sm,
              paddingHorizontal: spacing.lg,
              borderRadius: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            })}
            onPress={() => setShowSubscriptionManagement(false)}
          >
            <CustomText style={{
              color: colors.dark,
              fontSize: 16,
              fontWeight: '600',
              fontFamily: 'Nunito',
              textAlign: 'center',
            }}>
              Close
            </CustomText>
          </Pressable>
        </View>
      </View>
    );
  };

  return (
    <Toast.Provider>
      <View
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: isMobile ? spacing.md : spacing.xl,
          paddingLeft: isMobile ? spacing.lg : spacing.xl,
          paddingRight: isMobile ? spacing.lg : spacing.xl,
          marginBottom: spacing.xxl,
        }}
      >
        <View
          style={{
            width: "100%",
            maxWidth: 1400,
            paddingTop: isMobile ? spacing.xxl : spacing.xxl * 1.25,
            paddingBottom: isMobile ? spacing.xxl : 0,
          }}
        >
          {/* Card for heading and subtitle */}
          <View
            style={{
              backgroundColor: colors.white,
              borderRadius: 16,
              padding: isMobile ? spacing.md : spacing.xl,
              marginBottom: spacing.xl,
              width: "100%",
              alignItems: "center",
              border: "3px solid black",
            }}
          >
            <CustomText
              style={{
                color: colors.dark,
                textAlign: "center",
                width: "100%",
                fontFamily: "Nunito",
                fontWeight: 400,
                fontSize: isMobile ? 24 : 32,
              }}
            >
              You are primarily a
              {dominantCategory.toLowerCase().startsWith("a") ||
              dominantCategory.toLowerCase().startsWith("e") ||
              dominantCategory.toLowerCase().startsWith("i") ||
              dominantCategory.toLowerCase().startsWith("o") ||
              dominantCategory.toLowerCase().startsWith("u")
                ? "n "
                : ""}
            </CustomText>
            <CustomText
              style={{
                fontSize: isMobile ? 80 : 120,
                fontWeight: "700",
                color: colors.dark,
                textAlign: "center",
                width: "100%",
                fontFamily: "Nunito",
              }}
            >
              {dominantCategory}
            </CustomText>
            <CustomText
              style={{
                fontSize: isMobile ? 24 : 32,
                color: colors.gray[700],
                textAlign: "center",
                width: "100%",
                marginBottom: isMobile ? spacing.xl : spacing.xxl,
                fontFamily: "Nunito",
              }}
            >
              {mainSection.Subtext}
            </CustomText>
            <CustomText
              style={{
                fontSize: isMobile ? 18 : 24,
                color: colors.gray[700],
                lineHeight: isMobile ? 24 : 28,
                marginBottom: isMobile ? spacing.md : spacing.lg,
                fontFamily: "Nunito",
                textAlign: "center",
                width: "90%",
              }}
            >
              {mainSection.Overview}
            </CustomText>

            {mainSection.DrivenBy && (
              <View style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: isMobile ? spacing.sm : spacing.lg,
                marginBottom: spacing.lg,
                width: "90%",
              }}>
                {[
                  { label: "Driven By", value: mainSection.DrivenBy },
                  { label: "Measures Success By", value: mainSection.MeasuresSuccessBy },
                  { label: "Natural Strength", value: mainSection.NaturalStrength },
                ].map(({ label, value }) => (
                  <View key={label} style={{
                    backgroundColor: colors.gray[100],
                    borderRadius: 12,
                    padding: spacing.md,
                    paddingHorizontal: spacing.lg,
                    alignItems: "center",
                    minWidth: isMobile ? "100%" : 180,
                    flex: isMobile ? undefined : 1,
                  }}>
                    <CustomText style={{
                      fontSize: 12, fontWeight: "600", color: colors.gray[500],
                      fontFamily: "Nunito", textTransform: "uppercase", letterSpacing: 1,
                      marginBottom: 4,
                    }}>{label}</CustomText>
                    <CustomText style={{
                      fontSize: isMobile ? 16 : 18, fontWeight: "700", color: colors.dark,
                      fontFamily: "Nunito", textAlign: "center",
                    }}>{value}</CustomText>
                  </View>
                ))}
              </View>
            )}

            {mainSection.WatchOut && (
              <View style={{
                backgroundColor: colors.warning + "15",
                border: `1px solid ${colors.warning}40`,
                borderRadius: 12,
                padding: spacing.md,
                paddingHorizontal: spacing.lg,
                marginBottom: spacing.md,
                width: "90%",
              }}>
                <CustomText style={{
                  fontSize: 12, fontWeight: "600", color: colors.warning,
                  fontFamily: "Nunito", textTransform: "uppercase", letterSpacing: 1,
                  marginBottom: 4,
                }}>Watch-Out</CustomText>
                <CustomText style={{
                  fontSize: isMobile ? 14 : 16, color: colors.gray[700],
                  fontFamily: "Nunito", lineHeight: isMobile ? 22 : 24,
                }}>{mainSection.WatchOut}</CustomText>
              </View>
            )}

            {sfAccount && (
              <View
                style={{
                  backgroundColor: colors.green + "20",
                  padding: isMobile ? spacing.xs : spacing.sm,
                  paddingLeft: isMobile ? spacing.md : spacing.lg,
                  paddingRight: isMobile ? spacing.md : spacing.lg,
                  borderRadius: 8,
                  marginTop: isMobile ? 8 : 8,
                  alignSelf: "center",
                }}
              >
                <CustomText
                  style={{
                    color: colors.green,
                    textAlign: "center",
                    whiteSpace: isMobile ? "normal" : "nowrap",
                    fontSize: isMobile ? 18 : 24,
                    fontFamily: "Nunito",
                  }}
                >
                  Your advisor has received your results!
                </CustomText>
              </View>
            )}

            {/* Subscription Status Display */}
            {subscriptionStatus && (
              <View
                style={{
                  backgroundColor: subscriptionStatus === 'active' 
                    ? colors.green + "20" 
                    : subscriptionStatus === 'cancelled' 
                      ? colors.warning + "20" 
                      : colors.danger + "20",
                  padding: isMobile ? spacing.xs : spacing.sm,
                  paddingLeft: isMobile ? spacing.md : spacing.lg,
                  paddingRight: isMobile ? spacing.md : spacing.lg,
                  borderRadius: 8,
                  marginTop: isMobile ? 8 : 8,
                  alignSelf: "center",
                }}
              >
                <CustomText
                  style={{
                    color: subscriptionStatus === 'active' 
                      ? colors.green 
                      : subscriptionStatus === 'cancelled' 
                        ? colors.warning 
                        : colors.danger,
                    textAlign: "center",
                    whiteSpace: isMobile ? "normal" : "nowrap",
                    fontSize: isMobile ? 18 : 24,
                    fontFamily: "Nunito",
                  }}
                >
                  {subscriptionStatus === 'active' 
                    ? '✅ Active Subscription' 
                    : subscriptionStatus === 'cancelled' 
                      ? '⏳ Subscription Cancelled (active until period end)' 
                      : subscriptionStatus === 'past_due'
                        ? '⚠️ Payment Issue - Please Update Payment Method'
                        : `📋 Subscription Status: ${subscriptionStatus}`}
                </CustomText>
              </View>
            )}

            {/* Subscription Management Button */}
            {subscriptionStatus === 'active' && (
              <View
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  gap: spacing.md,
                  marginTop: spacing.md,
                }}
              >
                <Pressable
                  style={({ hovered }: { hovered: boolean }) => ({
                    backgroundColor: hovered ? "#5a6c7a" : colors.gray[600],
                    paddingVertical: spacing.xs,
                    paddingHorizontal: spacing.md,
                    borderRadius: 8,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  })}
                  onPress={handleOpenCustomerPortal}
                >
                  <CustomText
                    style={{
                      color: colors.white,
                      fontSize: isMobile ? 14 : 16,
                      fontWeight: "600",
                      fontFamily: "Nunito",
                    }}
                  >
                    Manage Subscription
                  </CustomText>
                </Pressable>
                <Pressable
                  style={({ hovered }: { hovered: boolean }) => ({
                    backgroundColor: hovered ? "#c53030" : colors.danger,
                    paddingVertical: spacing.xs,
                    paddingHorizontal: spacing.md,
                    borderRadius: 8,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  })}
                  onPress={() => setShowSubscriptionManagement(true)}
                >
                  <CustomText
                    style={{
                      color: colors.white,
                      fontSize: isMobile ? 14 : 16,
                      fontWeight: "600",
                      fontFamily: "Nunito",
                    }}
                  >
                    Cancel Subscription
                  </CustomText>
                </Pressable>
              </View>
            )}
          </View>

          {/* Card for main quiz graph results */}
          <View
            style={{
              backgroundColor: colors.white,
              borderRadius: 16,
              padding: isMobile ? spacing.md : spacing.xl,
              marginBottom: spacing.xl,
              width: "100%",
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-around",
              border: "3px solid black",
            }}
          >
            {/* Each category and their relative contributions */}
            <View
              style={{
                width: isMobile ? "100%" : "50%",
                padding: isMobile ? spacing.md : spacing.xl,
              }}
            >
              {sortedCategories.map((category) => (
                <View key={category} style={{ marginBottom: 24 }}>
                  <View
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      marginBottom: 4,
                    }}
                  >
                    <CustomText style={{ fontFamily: "Nunito", fontSize: 24 }}>
                      {category}
                    </CustomText>
                    <CustomText
                      style={{ color: "#717171", fontWeight: 400, fontSize: 18 }}
                    >
                      {Math.round(completeMainCategoryScores[category])}%
                    </CustomText>
                  </View>
                  <CatContributionBar
                    value={Math.round(completeMainCategoryScores[category])}
                    color={CATEGORY_COLORS[category] || colors.primary}
                  />
                </View>
              ))}
            </View>
            {/* The pie chart graph of all categories */}
            <View
              style={{
                width: isMobile ? "100%" : "50%",
                justifyContent: "center",
              }}
            >
              <ResultsGraph graphData={completeMainCategoryScores} type="main" />
            </View>
          </View>

          {/* Paywall Overlay for Detailed Results */}
          <PaywallOverlay isVisible={showPaywall} onUnlock={handleUnlock}>
            {/* Detailed Personality Analysis Section */}
            <View
              style={{
                width: "100%",
                margin: "0 auto",
                maxHeight: showPaywall ? 1550 : undefined,
                overflowY: showPaywall ? "hidden" : undefined,
              }}
            >
              {/* Traits Section - Adjust trait container styles */}
              <View
                style={{
                  width: "100%",
                  margin: "0 auto",
                  marginBottom: spacing.xl,
                  backgroundColor: colors.white,
                  borderRadius: 16,
                  paddingHorizontal: isMobile ? spacing.lg : spacing.xxl,
                  paddingVertical: isMobile ? spacing.md : spacing.lg,
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: isMobile ? "flex-start" : "center",
                  justifyContent: "space-between",
                  border: "3px solid black",
                }}
              >
                <View style={{ flex: 1, minWidth: 0, width: "100%" }}>
                  <View
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: spacing.sm,
                      marginBottom: isMobile ? spacing.sm : spacing.md,
                    }}
                  >
                    <CustomText
                      style={{
                        fontSize: isMobile ? 36 : 48,
                        fontWeight: "700",
                        color: colors.dark,
                        fontFamily: "Nunito",
                        textAlign: "center",
                      }}
                    >
                      Dominant traits
                    </CustomText>
                  </View>
                  <View
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      flexWrap: "wrap",
                      alignItems: "flex-start",
                      gap: spacing.sm,
                      marginBottom: isMobile ? spacing.md : spacing.lg,
                      width: "100%",
                    }}
                  >
                    {mainSection.DominantTraits.map(
                      (trait, i) => (
                        <View
                          key={i}
                          style={{
                            backgroundColor: colors.gray[100],
                            paddingHorizontal: spacing.sm,
                            paddingVertical: spacing.xs,
                            borderRadius: 4,
                            marginBottom: spacing.xs,
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          <CustomText
                            numberOfLines={isMobile ? 0 : 1}
                            style={{
                              fontSize: isMobile ? 18 : 24,
                              color: colors.gray[700],
                              fontFamily: "Nunito",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                            }}
                          >
                            {trait}
                          </CustomText>
                        </View>
                      )
                    )}
                  </View>
                </View>
              </View>

              {/* Strengths, Weaknesses, and Sub-Personality Section */}
              <View
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: isMobile ? "center" : "stretch",
                  width: "100%",
                  gap: isMobile ? 0 : spacing.md,
                  margin: "0 auto",
                  marginBottom: isMobile ? spacing.sm : spacing.md,
                }}
              >
                {/* Left half is strengths and weaknesses */}
                <View
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {/* Strengths Section - Apply similar width and wrapping fixes */}
                  <View
                    style={{
                      width: "100%",
                      marginBottom: isMobile ? spacing.xl : spacing.md,
                      backgroundColor: "#ebf7ed",
                      borderRadius: 16,
                      padding: isMobile ? spacing.lg : spacing.xl,
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      alignItems: isMobile ? "flex-start" : "center",
                      justifyContent: "space-between",
                      border: "3px solid #b1ddb4",
                    }}
                  >
                    <View style={{ flex: 1, minWidth: 0, width: "100%" }}>
                      <View
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: spacing.sm,
                          marginBottom: isMobile ? spacing.sm : spacing.md,
                        }}
                      >
                        <CustomText
                          style={{
                            fontSize: isMobile ? 36 : 48,
                            fontWeight: "700",
                            color: colors.dark,
                            fontFamily: "Nunito",
                          }}
                        >
                          ✅ Strengths
                        </CustomText>
                      </View>
                      <View
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          flexWrap: "wrap",
                          gap: spacing.sm,
                          alignItems: "flex-start",
                          marginBottom: isMobile ? spacing.md : spacing.lg,
                          width: "100%",
                        }}
                      >
                        {mainSection.Strengths.map(
                          (strength, i) => (
                            <View
                              key={i}
                              style={{
                                paddingHorizontal: spacing.sm,
                                paddingVertical: spacing.xs,
                                borderRadius: 4,
                                marginBottom: spacing.xs,
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                gap: 10,
                                color: colors.success,
                              }}
                            >
                              <CustomText
                                style={{
                                  alignSelf: "flex-start",
                                  paddingTop: 5,
                                  fontSize: isMobile ? 30 : 26,
                                  color: colors.success,
                                }}
                              >
                                &#x2713;
                              </CustomText>
                              <CustomText
                                numberOfLines={isMobile ? 0 : 1}
                                style={{
                                  fontSize: isMobile ? 18 : 24,
                                  color: colors.success,
                                  fontFamily: "Nunito",
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                  overflowWrap: "break-word",
                                }}
                              >
                                {strength}
                              </CustomText>
                            </View>
                          )
                        )}
                      </View>
                    </View>
                  </View>

                  {/* Challenges Section - Apply similar width and wrapping fixes */}
                  <View
                    style={{
                      width: "100%",
                      marginBottom: isMobile ? spacing.xl : spacing.md,
                      backgroundColor: "#fffbe8",
                      borderRadius: 16,
                      padding: isMobile ? spacing.lg : spacing.xl,
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      alignItems: isMobile ? "flex-start" : "center",
                      justifyContent: "space-between",
                      border: "3px solid #f4e67d",
                    }}
                  >
                    <View style={{ flex: 1, minWidth: 0, width: "100%" }}>
                      <View
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: spacing.sm,
                          marginBottom: isMobile ? spacing.sm : spacing.md,
                        }}
                      >
                        <CustomText
                          style={{
                            fontSize: isMobile ? 36 : 48,
                            fontWeight: "700",
                            color: colors.dark,
                            fontFamily: "Nunito",
                          }}
                        >
                          ⚠️ Weaknesses
                        </CustomText>
                      </View>
                      <View
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          flexWrap: "wrap",
                          gap: spacing.sm,
                          alignItems: "flex-start",
                          marginBottom: isMobile ? spacing.md : spacing.lg,
                          width: "100%",
                        }}
                      >
                        {mainSection.Weaknesses.map(
                          (challenge, i) => (
                            <View
                              key={i}
                              style={{
                                paddingHorizontal: spacing.sm,
                                paddingVertical: spacing.xs,
                                borderRadius: 4,
                                marginBottom: spacing.xs,
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                gap: 10,
                                color: colors.danger,
                              }}
                            >
                              <View
                                style={{ alignSelf: "flex-start", paddingTop: 5 }}
                              >
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 15 15"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M8.4449 0.608765C8.0183 -0.107015 6.9817 -0.107015 6.55509 0.608766L0.161178 11.3368C-0.275824 12.07 0.252503 13 1.10608 13H13.8939C14.7475 13 15.2758 12.07 14.8388 11.3368L8.4449 0.608765ZM7.4141 1.12073C7.45288 1.05566 7.54712 1.05566 7.5859 1.12073L13.9798 11.8488C14.0196 11.9154 13.9715 12 13.8939 12H1.10608C1.02849 12 0.980454 11.9154 1.02018 11.8488L7.4141 1.12073ZM6.8269 4.48611C6.81221 4.10423 7.11783 3.78663 7.5 3.78663C7.88217 3.78663 8.18778 4.10423 8.1731 4.48612L8.01921 8.48701C8.00848 8.766 7.7792 8.98664 7.5 8.98664C7.2208 8.98664 6.99151 8.766 6.98078 8.48701L6.8269 4.48611ZM8.24989 10.476C8.24989 10.8902 7.9141 11.226 7.49989 11.226C7.08567 11.226 6.74989 10.8902 6.74989 10.476C6.74989 10.0618 7.08567 9.72599 7.49989 9.72599C7.9141 9.72599 8.24989 10.0618 8.24989 10.476Z"
                                    fill="currentColor"
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                  ></path>
                                </svg>
                              </View>
                              <CustomText
                                numberOfLines={isMobile ? 0 : 1}
                                style={{
                                  fontSize: isMobile ? 18 : 24,
                                  color: colors.danger,
                                  fontFamily: "Nunito",
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                  overflowWrap: "break-word",
                                }}
                              >
                                {challenge}
                              </CustomText>
                            </View>
                          )
                        )}
                      </View>
                    </View>
                  </View>
                </View>

                {/* The sub-personality analysis panel (subset graph, "You are a
                    <sub-type>!", per-sub-type contribution bars and the sub-type
                    definition) was REMOVED on 2026-08-20 — ClickUp 86bb0f9vz.
                    McKayla: "They don't need to know if they're a subtype."
                    Everything below now renders the CORE personality's content. */}
              </View>

              {/* Famous person, growth opportunities, and behavioral concepts sections */}
              <View
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: isMobile ? "center" : "stretch",
                  width: "100%",
                  gap: isMobile ? 0 : spacing.md,
                  margin: "0 auto",
                }}
              >
                {/* Right half is growth opportunities and behavioral concepts */}
                <View
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minWidth: 0,
                    width: "100%",
                  }}
                >
                  {/* Growth opportunities section */}
                  <View
                    style={{
                      width: "100%",
                      marginBottom: isMobile ? spacing.xl : spacing.md,
                      backgroundColor: "white",
                      borderRadius: 16,
                      padding: isMobile ? spacing.lg : spacing.xl,
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      alignItems: isMobile ? "flex-start" : "center",
                      justifyContent: "space-between",
                      border: "3px solid black",
                    }}
                  >
                    <View style={{ flex: 1, minWidth: 0, width: "100%" }}>
                      <View
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: spacing.sm,
                          marginBottom: isMobile ? spacing.sm : spacing.md,
                        }}
                      >
                        <CustomText
                          style={{
                            fontSize: isMobile ? 36 : 48,
                            fontWeight: "700",
                            color: colors.dark,
                            fontFamily: "Nunito",
                          }}
                        >
                          🌱 Growth Opportunities
                        </CustomText>
                      </View>
                      <View
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          flexWrap: "wrap",
                          gap: spacing.sm,
                          alignItems: "flex-start",
                          marginBottom: isMobile ? spacing.md : spacing.lg,
                          width: "100%",
                        }}
                      >
                        {mainSection.GrowthOpportunities.map((challenge, i) => (
                          <View
                            key={i}
                            style={{
                              backgroundColor: colors.green + "20",
                              paddingHorizontal: spacing.sm,
                              paddingVertical: spacing.xs,
                              borderRadius: 4,
                              marginBottom: spacing.xs,
                            }}
                          >
                            <CustomText
                              numberOfLines={isMobile ? 0 : 1}
                              style={{
                                fontSize: isMobile ? 18 : 24,
                                color: colors.green,
                                fontFamily: "Nunito",
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                              }}
                            >
                              {challenge}
                            </CustomText>
                          </View>
                        ))}
                      </View>
                    </View>
                  </View>
                  {/* Behavioral concepts section */}
                  <View
                    style={{
                      width: "100%",
                      marginBottom: isMobile ? spacing.xl : spacing.md,
                      backgroundColor: "white",
                      borderRadius: 16,
                      padding: isMobile ? spacing.lg : spacing.xl,
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      alignItems: isMobile ? "flex-start" : "center",
                      justifyContent: "space-between",
                      border: "3px solid black",
                    }}
                  >
                    <View style={{ flex: 1, minWidth: 0, width: "100%" }}>
                      <View
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: spacing.sm,
                          marginBottom: isMobile ? spacing.sm : spacing.md,
                        }}
                      >
                        <CustomText
                          style={{
                            fontSize: isMobile ? 36 : 48,
                            fontWeight: "700",
                            color: colors.dark,
                            fontFamily: "Nunito",
                          }}
                        >
                          🧠 Behavioral Concepts
                        </CustomText>
                      </View>
                      <View
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          flexWrap: "wrap",
                          gap: spacing.sm,
                          alignItems: "flex-start",
                          marginBottom: isMobile ? spacing.md : spacing.lg,
                          width: "100%",
                        }}
                      >
                        {mainSection.BehavioralConcepts.map((challenge, i) => (
                          <View
                            key={i}
                            style={{
                              backgroundColor: colors.pink + "20",
                              paddingHorizontal: spacing.sm,
                              paddingVertical: spacing.xs,
                              borderRadius: 4,
                              marginBottom: spacing.xs,
                            }}
                          >
                            <CustomText
                              numberOfLines={isMobile ? 0 : 1}
                              style={{
                                fontSize: isMobile ? 18 : 24,
                                color: colors.pink,
                                fontFamily: "Nunito",
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                              }}
                            >
                              {challenge}
                            </CustomText>
                          </View>
                        ))}
                      </View>
                    </View>
                  </View>
                </View>
              </View>
              {/* 5-Step Guided Action Plan — interactive workbook (86badfk6e).
                  Phase 1: exercise 1 (Reflective Prompt) is interactive + persisted;
                  exercises 2-5 render as "coming soon". Content from category_mappings. */}
              {specificExercises.length > 0 ? (
                <GuidedActionPlan
                  personality={dominantCategory}
                  exercises={specificExercises}
                  quizId={quizId}
                />
              ) : (
                // ⛔ SAY SO — do not render nothing. Exercises are still keyed to
                // the retired sub-personalities, so every result created after
                // 2026-08-20 has none until McKayla's re-authored workbook lands.
                // Rendering an empty region would read as "your plan is empty",
                // which is a claim about the user rather than about us.
                <View
                  style={{
                    width: "100%",
                    backgroundColor: "white",
                    borderRadius: 16,
                    padding: isMobile ? spacing.lg : spacing.xl,
                    marginBottom: isMobile ? spacing.xl : spacing.md,
                  }}
                >
                  <CustomText
                    style={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      fontSize: isMobile ? 20 : 24,
                      color: colors.dark,
                    }}
                  >
                    Your guided action plan is being rewritten
                  </CustomText>
                  <CustomText
                    style={{
                      fontFamily: "Nunito",
                      fontSize: isMobile ? 16 : 18,
                      color: colors.gray[700],
                      marginTop: spacing.sm,
                    }}
                  >
                    We are rebuilding the workbook exercises around the five Money
                    Personalities. Your results above are complete — the exercises
                    will appear here once the new set is ready.
                  </CustomText>
                </View>
              )}
              {/* Download PDF Results Button (Only if user has paid) */}
              <View style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}>
                {/* Add'l Resources button */}
                <Pressable 
                  style={({ hovered }: { hovered: boolean }) => ({
                    backgroundColor: 'transparent',
                    width: '100%',
                    cursor: 'pointer',
                  })}
                  onPress={() => {
                    navigate("/about", {
                      state: { ...resultsData, from: location.pathname },
                    });
                  }}
                >
                  {({ hovered }: { hovered: boolean }) => (
                    <CustomText style={{
                      fontFamily: "Nunito",
                      fontWeight: 600,
                      fontSize: isMobile ? 18 : 24,
                      textAlign: 'center',
                      color: hovered ? '#44505d' : 'black',
                      transition: "all 0.2s ease",
                    }}>
                      <u>Additional Resources &gt;&gt; </u>
                    </CustomText>
                  )}
                </Pressable>
                {(() => {
                  const personalInfo = localStorage.getItem('mpay_personalInfo');
                  const hasCompletedInfoCollection = personalInfo !== null;
                  
                  if (hasCompletedInfoCollection) {
                    // User has completed info collection - show direct PDF download
                    let parsedPersonalInfo = undefined;
                    try {
                      parsedPersonalInfo = personalInfo ? JSON.parse(personalInfo) : undefined;
                    } catch (error) {
                      console.error('Error parsing personal info from localStorage:', error);
                      parsedPersonalInfo = undefined;
                    }
                    
                    return (
                      <PDFDownloadLink
                        document={
                          <PersonalityTestPDF
                            answers={answers || []}
                            categoryScores={categoryScores || {}}
                            dominantCategory={dominantCategory || ""}
                            personalInfo={parsedPersonalInfo}
                          />
                        }
                        fileName={`financial-personality-profile-${
                          new Date().toISOString().split("T")[0]
                        }.pdf`}
                        style={{
                          textDecoration: "none",
                          color: "inherit",
                          width: isMobile ? '100%' : '50%',
                        }}
                      >
                        {({ loading: boolean }) => (
                          <Pressable
                            style={({ hovered }: { hovered: boolean }) => ({
                              backgroundColor: hovered ? "#66727f" : colors.dark,
                              paddingVertical: isMobile ? spacing.sm : spacing.md,
                              paddingHorizontal: isMobile ? spacing.xl : spacing.xxl,
                              borderRadius: 12,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "100%",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              color: "white",
                              fontFamily: "Nunito",
                              fontSize: isMobile ? 18 : 24,
                              marginTop: isMobile ? spacing.sm : spacing.md,
                            })}
                          >
                            <CustomText
                              style={{
                                color: colors.white,
                                fontSize: isMobile ? 18 : 24,
                                fontWeight: "600",
                                fontFamily: "Nunito",
                              }}
                            >
                              {boolean ? "Generating PDF..." : "Download Personality PDF"}
                            </CustomText>
                          </Pressable>
                        )}
                      </PDFDownloadLink>
                    );
                  } else {
                    // User hasn't completed info collection - navigate to form
                    return (
                      <Pressable
                        style={({ hovered }: { hovered: boolean }) => ({
                          backgroundColor: hovered ? "#66727f" : colors.dark,
                          paddingVertical: isMobile ? spacing.sm : spacing.md,
                          paddingHorizontal: isMobile ? spacing.xl : spacing.xxl,
                          borderRadius: 12,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: isMobile ? '100%' : '50%',
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          color: "white",
                          fontFamily: "Nunito",
                          fontSize: isMobile ? 18 : 24,
                          marginTop: isMobile ? spacing.sm : spacing.md,
                        })}
                        onPress={() => {
                          navigate("/quiz/results/informationform", {
                            state: { ...resultsData, from: location.pathname },
                          });
                        }}
                      >
                        <CustomText
                          style={{
                            color: colors.white,
                            fontSize: isMobile ? 18 : 24,
                            fontWeight: "600",
                            fontFamily: "Nunito",
                          }}
                        >
                          Download Personality PDF
                        </CustomText>
                      </Pressable>
                    );
                  }
                })()}
              </View>
            </View>
          </PaywallOverlay>
        </View>
        <ConnectProfessionalModal
          isVisible={showConnectModal}
          onClose={() => setShowConnectModal(false)}
        />
        <PaymentOverlay
          isVisible={showPaymentPending}
          paymentStatus={paymentStatus}
          paymentLoading={paymentLoading}
          paymentError={paymentError}
          paymentUrl={paymentUrl}
          onOpenPayment={handleOpenPayment}
          onCancelPayment={handleCancelPayment}
        />

        {/* Subscription Management Modal */}
        {showSubscriptionManagement && (
          renderSubscriptionManagement()
        )}

        {/* Success Toast */}
        <Toast.Root open={showPaymentSuccess} onOpenChange={setShowPaymentSuccess} duration={1500} style={{ background: colors.success, color: 'white', borderRadius: 8, padding: 16, fontFamily: 'Nunito', fontSize: 18, boxShadow: '0 2px 10px rgba(0,0,0,0.15)' }}>
            <Toast.Title className="font-semibold text-lg font-nunito">
              Payment Successful! You can now see your results.
            </Toast.Title>
          </Toast.Root>
          

          <Toast.Viewport style={{ position: 'fixed', bottom: 40, left: 0, right: 0, display: 'flex', justifyContent: 'center', zIndex: 9999 }} />
      </View>
    </Toast.Provider>
  );
}
